import { Fragment, useState } from "react";
import clsx from "clsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, LifeBuoy, Loader2, Send } from "lucide-react";
import { api, ApiError, isAdmin, type SupportKind, type SupportRequest, type SupportStatus, type User } from "../lib/api";
import { stamp } from "../lib/format";

/**
 * Report a problem or ask for a feature, without leaving the app.
 *
 * The owner asked for this long ago: a duty manager who hits something wrong at 05:00
 * should be able to say so where it happened, not find an email address. Each request
 * is kept on the server and emailed to the product team; the sender sees it below with
 * its status and whatever an administrator wrote back. Administrators see every request
 * and mark them seen or done.
 */

export const KINDS: Array<{ id: SupportKind; label: string }> = [
  { id: "bug", label: "Problem" },
  { id: "feature", label: "Feature request" },
  { id: "question", label: "Question" },
];

export const STATUSES: Array<{ id: SupportStatus; label: string; tone: string }> = [
  { id: "new", label: "New", tone: "border-info/50 text-info" },
  { id: "seen", label: "Seen", tone: "border-warn/50 text-warn" },
  { id: "done", label: "Done", tone: "border-ok/50 text-ok" },
];

/** The screens as the Support page names them, from the view ids App.tsx uses. The server keeps the same list in support.ts for the email. */
export const PAGE_NAMES: Record<string, string> = {
  board: "Board",
  ramp: "Ramp",
  schedule: "Schedule",
  forecast: "Forecast",
  bags: "Bags",
  history: "History",
  rules: "Rules",
  settings: "Settings",
  support: "Support",
  help: "Help",
};

const card = "rounded-xl border border-line p-4";
const input = "w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[12.5px] outline-none focus:border-ink-3";
const th = "px-2 py-1.5 text-left text-[11px] font-semibold tracking-wide text-ink-3 uppercase";
const td = "px-2 py-1.5 align-top";
const small = "rounded-md border border-line px-2 py-0.5 text-[12px] font-semibold hover:border-ink-3 disabled:opacity-40";

export function kindLabel(kind: SupportKind): string {
  return KINDS.find((k) => k.id === kind)?.label ?? kind;
}

export function StatusChip({ status }: { status: SupportStatus }) {
  const s = STATUSES.find((x) => x.id === status) ?? STATUSES[0]!;
  return <span className={clsx("rounded-full border px-2 py-0.5 text-[11px] font-semibold", s.tone)}>{s.label}</span>;
}

/** The form on its own, so it can be rendered without a server behind it. */
export function SupportForm({
  from,
  sending,
  sent,
  error,
  onSend,
}: {
  /** The view id the person came from, pre-filled as the screen the request is about. */
  from: string;
  sending: boolean;
  /** What the server answered after the last send, shown under the button. */
  sent: { emailed: boolean; message: string } | null;
  error: string | null;
  onSend: (kind: SupportKind, title: string, details: string, page: string) => void;
}) {
  const [kind, setKind] = useState<SupportKind>("bug");
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [page, setPage] = useState(PAGE_NAMES[from] ? from : "board");
  const ready = title.trim().length > 0 && details.trim().length > 0 && !sending;
  return (
    <form
      className={card}
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        // The text stays until the server has it: the parent remounts the form on success.
        onSend(kind, title.trim(), details.trim(), page);
      }}
    >
      <div className="mb-1 text-[13.5px] font-semibold">Tell us what happened or what you need</div>
      <p className="mb-3 text-[12.5px] text-ink-3">It goes to the product team by email and stays here with its status. Say which flight or stand, and what you expected to see.</p>
      <div className="mb-3 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Kind">
        {KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            role="radio"
            aria-checked={kind === k.id}
            className={clsx("rounded-full border px-3 py-1 text-[12px] font-semibold", kind === k.id ? "border-ink bg-panel-2 text-ink" : "border-line text-ink-2 hover:border-ink-3")}
            onClick={() => setKind(k.id)}
          >
            {k.label}
          </button>
        ))}
      </div>
      <label className="mb-2 block text-[12px] text-ink-2">
        Title
        <input className={clsx(input, "mt-1")} maxLength={120} placeholder={kind === "feature" ? "What would help, in one line" : "What went wrong, in one line"} value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className="mb-2 block text-[12px] text-ink-2">
        Details
        <textarea
          className={clsx(input, "mt-1 min-h-28")}
          maxLength={4000}
          placeholder="What you did, what you expected, what you saw instead. Name the flight or the stand, and the day, if there is one."
          value={details}
          onChange={(e) => setDetails(e.target.value)}
        />
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5 text-[12px] text-ink-2">
          Screen
          <select className="rounded-md border border-line bg-panel px-2 py-1 text-[12.5px]" value={page} onChange={(e) => setPage(e.target.value)} aria-label="Which screen this is about">
            {Object.entries(PAGE_NAMES).map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="ml-auto flex items-center gap-1.5 rounded-lg bg-ink px-3 py-1.5 text-[12.5px] font-semibold text-panel disabled:opacity-40" disabled={!ready}>
          {sending ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />} Send
        </button>
      </div>
      {sent && (
        <p className="mt-2 text-[12.5px] text-ok">
          Sent. We read every one; you will see it below with its status.
          {!sent.emailed && <span className="text-ink-3"> (It was not emailed: {sent.message})</span>}
        </p>
      )}
      {error && <p className="mt-2 text-[12.5px] text-bad">{error}</p>}
    </form>
  );
}

/**
 * The rows, for the sender or the administrator. The sender sees the kind, the title,
 * the status and the note; an administrator can open each row for the details and the
 * context, and change its status or write the note.
 */
export function RequestList({
  rows,
  tz,
  admin,
  onUpdate,
}: {
  rows: SupportRequest[];
  tz: string;
  admin?: boolean;
  onUpdate?: (id: number, patch: { status?: SupportStatus; note?: string }) => void;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const [notes, setNotes] = useState<Record<number, string>>({});
  if (!rows.length) return <p className="text-[12.5px] text-ink-3">{admin ? "Nothing received yet." : "You have not sent anything yet."}</p>;
  return (
    <table className="w-full text-[12.5px]">
      <thead>
        <tr className="border-b border-line">
          <th className={th}>When</th>
          {admin && <th className={th}>Who</th>}
          <th className={th}>Kind</th>
          <th className={th}>Title</th>
          <th className={th}>Status</th>
          {admin && <th className={th}></th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <Fragment key={r.id}>
            <tr className="border-b border-line">
              <td className={clsx(td, "tabular whitespace-nowrap")}>{stamp(r.at, tz)}</td>
              {admin && (
                <td className={clsx(td, "whitespace-nowrap")}>
                  {r.user.name ?? r.user.email ?? "?"}
                  {r.role && <span className="ml-1 text-ink-3 capitalize">{r.role}</span>}
                </td>
              )}
              <td className={clsx(td, "whitespace-nowrap text-ink-2")}>{kindLabel(r.kind)}</td>
              <td className={td}>
                <div className="font-semibold">{r.title}</div>
                {r.note && <div className="mt-0.5 text-ink-2">Reply: {r.note}</div>}
              </td>
              <td className={clsx(td, "whitespace-nowrap")}>
                <StatusChip status={r.status} />
              </td>
              {admin && (
                <td className={clsx(td, "whitespace-nowrap text-right")}>
                  <button className={clsx(small, "inline-flex items-center gap-1")} onClick={() => setOpen(open === r.id ? null : r.id)} aria-expanded={open === r.id}>
                    {open === r.id ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />} Details
                  </button>
                </td>
              )}
            </tr>
            {admin && open === r.id && (
              <tr className="border-b border-line bg-panel-2/40">
                <td colSpan={6} className="px-3 py-2">
                  <div className="mb-2 whitespace-pre-wrap text-[12.5px]">{r.details}</div>
                  <div className="mb-2 text-[11.5px] text-ink-3">
                    {r.user.email && <span>{r.user.email} · </span>}
                    screen {r.page ? (PAGE_NAMES[r.page] ?? r.page) : "not said"}
                    {r.context?.appVersion && <span> · app {r.context.appVersion}</span>}
                    {r.context?.url && <span> · {r.context.url}</span>}
                    {r.context?.userAgent && <span> · {r.context.userAgent}</span>}
                    <span> · {r.emailed ? "emailed" : "not emailed"}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {STATUSES.map((s) => (
                      <button key={s.id} className={clsx(small, r.status === s.id && "bg-panel-2")} disabled={r.status === s.id} onClick={() => onUpdate?.(r.id, { status: s.id })}>
                        {s.label}
                      </button>
                    ))}
                    <input
                      className="min-w-48 flex-1 rounded-md border border-line bg-panel px-2 py-1 text-[12.5px] outline-none focus:border-ink-3"
                      placeholder="A line back to the sender"
                      maxLength={500}
                      value={notes[r.id] ?? r.note}
                      onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onUpdate?.(r.id, { note: (notes[r.id] ?? r.note).trim() });
                      }}
                      aria-label="Note to the sender"
                    />
                    <button className={small} disabled={(notes[r.id] ?? r.note) === r.note} onClick={() => onUpdate?.(r.id, { note: (notes[r.id] ?? r.note).trim() })}>
                      Save note
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

export function Support({ user, tz, from }: { user: User; tz: string; from: string }) {
  const qc = useQueryClient();
  const admin = isAdmin(user.role);
  const [filter, setFilter] = useState<SupportStatus | "">("");
  const q = useQuery({ queryKey: ["support"], queryFn: () => api.supportRequests() });
  const send = useMutation({
    mutationFn: (v: { kind: SupportKind; title: string; details: string; page: string }) => api.sendSupport(v.kind, v.title, v.details, v.page),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["support"] }),
  });
  const update = useMutation({
    mutationFn: (v: { id: number; patch: { status?: SupportStatus; note?: string } }) => api.updateSupport(v.id, v.patch),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["support"] }),
  });
  const rows = q.data?.rows ?? [];
  const mine = rows.filter((r) => r.user.id === user.id);
  const everything = filter ? rows.filter((r) => r.status === filter) : rows;

  return (
    <div className="scroll-thin h-full overflow-auto p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h1 className="mr-2 flex items-center gap-2 text-[18px] font-bold">
          <LifeBuoy className="size-5 text-ink-3" /> Support
        </h1>
        {q.isFetching && <Loader2 className="size-4 animate-spin text-ink-3" />}
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <SupportForm
          key={send.data?.id ?? 0}
          from={from}
          sending={send.isPending}
          sent={send.data ? { emailed: send.data.emailed, message: send.data.message } : null}
          error={send.error ? (send.error instanceof ApiError ? send.error.message : "Something went wrong") : null}
          onSend={(kind, title, details, page) => send.mutate({ kind, title, details, page })}
        />
        <div className={card}>
          <div className="mb-2 text-[13.5px] font-semibold">Your requests</div>
          <RequestList rows={mine} tz={tz} />
        </div>
      </div>
      {admin && (
        <div className={clsx(card, "mt-4")}>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <div className="text-[13.5px] font-semibold">Everything received</div>
            <select className="ml-auto rounded-md border border-line bg-panel px-2 py-1 text-[12.5px]" value={filter} onChange={(e) => setFilter(e.target.value as SupportStatus | "")} aria-label="Status">
              <option value="">Every status</option>
              {STATUSES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <p className="mb-2 text-[12px] text-ink-3">Open a row for the details and where it came from. Mark it Seen when someone has it, Done when it is answered; the note is shown to the sender.</p>
          <RequestList rows={everything} tz={tz} admin onUpdate={(id, patch) => update.mutate({ id, patch })} />
          {update.error && <p className="mt-2 text-[12px] text-bad">{update.error instanceof ApiError ? update.error.message : "Something went wrong"}</p>}
        </div>
      )}
    </div>
  );
}
